<script>
import { defineComponent, ref, onMounted, onUnmounted, computed, watch } from 'vue';
import { useI18n } from 'vue-i18n';

import { useDialogListener } from '@/composables';
import { capitalize } from 'lodash';
import { DateTime } from 'luxon';
import { LuxonDateTimeFormats } from '@nova/core';
import LimitsOverviewTab from '@/modules/appointments/components/limits/LimitsOverviewTab.vue';
import { isDateString, isDate } from 'class-validator';

export default defineComponent({
  name: 'EventTriggeredLimitsOverviewDrawer',
  components: {
    LimitsOverviewTab
  },
  setup() {
    const { t } = useI18n();

    const datePickerMenu = ref(false);

    const activeTab = ref('warehouse');
    const {
      isDialogOpen,
      dialogProps,
      closeDialog,
      resetDialogProps,
      subscribeToDialogEvent,
      unsubscribeFromDialogEvent
    } = useDialogListener('open-limits-overview-drawer');

    onMounted(() => {
      subscribeToDialogEvent();
    });

    onUnmounted(() => {
      unsubscribeFromDialogEvent();
    });

    const dateMode = ref(dialogProps.value?.dateMode?.toLowerCase() ?? 'day');
    const timezone = computed(() => dialogProps.value?.timezone);
    const initialDate = computed(() => dialogProps.value?.baseDate ?? DateTime.now().toISODate());
    const baseDate = ref(DateTime.fromISO(initialDate.value).setZone(timezone.value).toJSDate());
    const warehouseId = computed(() => dialogProps.value?.warehouseId);

    const formattedBaseDate = computed(() => {
      if (!baseDate.value) {
        return '--';
      }

      const dateFormat =
        dateMode.value === 'month' ? 'LLLL' : LuxonDateTimeFormats.LongDateShortMonth;

      return DateTime.fromJSDate(baseDate.value).toFormat(dateFormat);
    });

    const setBaseDate = newDate => {
      let newDateTimeObj;
      if (isDate(newDate)) {
        newDateTimeObj = DateTime.fromJSDate(newDate);
      }
      if (isDateString(newDate)) {
        newDateTimeObj = DateTime.fromISO(newDate);
      }

      if (!newDateTimeObj) {
        return DateTime.now().setZone(timezone.value).toJSDate();
      }

      baseDate.value = newDateTimeObj.setZone(timezone.value).toJSDate();
    };

    watch(initialDate, newInitialDate => {
      baseDate.value = DateTime.fromISO(newInitialDate).setZone(timezone.value).toJSDate();
    });

    return {
      t,
      activeTab,
      isDialogOpen,
      warehouseId,
      dateMode,
      baseDate,
      setBaseDate,
      formattedBaseDate,
      capitalize,
      closeDialog,
      resetDialogProps,
      datePickerMenu
    };
  }
});
</script>
<template>
  <v-dialog
    v-model="isDialogOpen"
    @keydown.escape="closeDialog"
    @after-leave="resetDialogProps"
    transition="slide-left"
    content-class="drawer-like"
    v-bind="$attrs">
    <v-card class="dialog-box" data-testid="limits-overview-dialog">
      <v-card-title class="d-flex flex-row align-center">
        <h2 class="typography-heading-lg">
          <v-icon icon="mdi-sort-reverse-variant" size="24" class="mr-2"></v-icon>
          {{ t('global.basicTerms.limits') }}
        </h2>
        <v-spacer></v-spacer>

        <v-btn variant="text" icon="mdi-close" @click="closeDialog" />
      </v-card-title>

      <v-card-text class="d-flex flex-column gap-6">
        <v-expansion-panels elevation="0" flat static readonly>
          <v-expansion-panel class="border border-radius">
            <div class="d-flex flex-row pa-4 gap-4 align-center">
              <strong>
                {{ t('appointment.dialogs.limitsOverviewDialog.messages.currentLimitsFor') }}:
              </strong>

              <v-select
                v-model="dateMode"
                :items="['day', 'month']"
                density="compact"
                variant="outlined"
                hide-details
                data-testid="unit-limiter-date-mode-select"
                class="date-mode-select"
                style="max-width: 120px">
                <template #selection="{ item }">
                  {{
                    capitalize(t(`appointment.dialogs.limitsOverviewDialog.labels.${item.value}`))
                  }}
                </template>
                <template #item="{ props, item }">
                  <v-list-item v-bind="props">
                    <template #title>
                      {{
                        capitalize(
                          t(`appointment.dialogs.limitsOverviewDialog.labels.${item.value}`)
                        )
                      }}
                    </template>
                  </v-list-item>
                </template>
              </v-select>

              <div>
                <v-menu v-model="datePickerMenu" :close-on-content-click="false" location="bottom">
                  <template #activator="{ props }">
                    <v-btn v-bind="props" variant="text" class="text-none">
                      <v-icon icon="mdi-calendar" size="16" class="mr-1" />
                      <strong>
                        {{ formattedBaseDate }}
                      </strong>
                    </v-btn>
                  </template>
                  <v-date-picker
                    :view-mode="dateMode === 'month' ? 'months' : 'month'"
                    type="date"
                    @update:model-value="datePickerMenu = false"
                    color="accent"
                    v-model="baseDate" />
                </v-menu>
              </div>
            </div>
          </v-expansion-panel>
        </v-expansion-panels>
        <div>
          <v-tabs v-model="activeTab" color="primary" class="custom-tabs">
            <v-tab value="warehouse" data-testid="unit-limiter-overview-tab-warehouse">
              {{ t('appointment.dialogs.limitsOverviewDialog.headers.warehouse') }}
            </v-tab>
            <v-tab value="dock" data-testid="unit-limiter-overview-tab-dock">
              {{ t('appointment.dialogs.limitsOverviewDialog.headers.dock') }}
            </v-tab>
            <v-tab value="loadtype" data-testid="unit-limiter-overview-tab-loadtype">
              {{ t('appointment.dialogs.limitsOverviewDialog.headers.loadType') }}
            </v-tab>
          </v-tabs>

          <v-tabs-window v-model="activeTab">
            <v-tabs-window-item value="warehouse">
              <limits-overview-tab
                v-if="activeTab === 'warehouse'"
                :warehouse-id="warehouseId"
                :base-date="baseDate"
                :date-mode="dateMode"
                unit-grouper-type="Warehouse"
                @close-drawer="closeDialog"
                class="my-6" />
            </v-tabs-window-item>
            <v-tabs-window-item value="dock">
              <limits-overview-tab
                v-if="activeTab === 'dock'"
                :warehouse-id="warehouseId"
                :base-date="baseDate"
                :date-mode="dateMode"
                unit-grouper-type="Dock"
                @close-drawer="closeDialog"
                class="my-6" />
            </v-tabs-window-item>
            <v-tabs-window-item value="loadtype">
              <limits-overview-tab
                v-if="activeTab === 'loadtype'"
                :warehouse-id="warehouseId"
                :base-date="baseDate"
                :date-mode="dateMode"
                unit-grouper-type="LoadType"
                @close-drawer="closeDialog"
                class="my-6" />
            </v-tabs-window-item>
          </v-tabs-window>
        </div>
      </v-card-text>
    </v-card>
  </v-dialog>
</template>
<style scoped lang="scss">
:deep(.drawer-like) {
  width: 620px;
  max-width: 100%;
  position: fixed;
  right: 0;
  margin: 0;
  height: 100vh;
  max-height: 100%;

  .v-card {
    border-start-end-radius: 0;
    border-end-end-radius: 0;
  }
}
</style>
